// src/i18n/load.js
import i18n from './index'

const loadedGlobals = new Set()
const loadedPages = new Set()

export async function ensureGlobals(locale) {
  const loc = locale || i18n.global.locale.value || 'en'
  if (loadedGlobals.has(loc)) return

  const msgs = (await import(
    /* webpackChunkName: "i18n-globals-[request]" */
    `./globals/${loc}.globals.json`
  )).default

  const current = i18n.global.getLocaleMessage(loc) || {}


  // existing checks
  validateMessagesTree(msgs, { locale: loc, ns: 'globals' })
  precompileAll(i18n, loc, null, msgs)

  i18n.global.setLocaleMessage(loc, { ...current, ...msgs })
  loadedGlobals.add(loc)
}


// --- helpers (add these here) ---
function validateMessagesTree(msgs, { locale, ns }) {
  const issues = []
  function checkStr(s, path) {
    const visible = s.replace(/\\\{/g, '⟨LBRACE⟩').replace(/\\\}/g, '⟨RBRACE⟩')
    let depth = 0, start = -1
    for (let i = 0; i < visible.length; i++) {
      const ch = visible[i]
      if (ch === '{') {
        if (depth === 1) issues.push({ path, kind: 'NESTED', frag: s })
        if (depth === 0) start = i
        depth++
      } else if (ch === '}') {
        if (depth === 0) {
          issues.push({ path, kind: 'UNBALANCED', frag: s })
        } else {
          const inner = visible.slice(start + 1, i).trim()
          if (!inner) issues.push({ path, kind: 'EMPTY', frag: s })
          else if (/,/.test(inner)) issues.push({ path, kind: 'ICU_COMMA', frag: `{${inner}}` })
          else if (!/^(\d+|[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z0-9_]+)*)$/.test(inner)) {
            issues.push({ path, kind: 'INVALID_TOKEN', frag: `{${inner}}` })
          }
        }
        depth = Math.max(0, depth - 1)
      }
    }
    if (depth > 0) issues.push({ path, kind: 'MISSING_CLOSE', frag: s })
  }
  function walk(node, path = []) {
    if (typeof node === 'string') return checkStr(node, path.join('.'))
    if (Array.isArray(node)) return node.forEach((v, i) => walk(v, path.concat(i)))
    if (node && typeof node === 'object') {
      Object.entries(node).forEach(([k, v]) => walk(v, path.concat(k)))
    }
  }
  walk(msgs)
  if (issues.length) {
    const lines = issues.map(it => `- ${it.kind} at "${it.path}" in ${locale}:${ns} — ${String(it.frag).slice(0,160)}`).join('\n')
    const err = new Error(`Invalid i18n placeholders detected:\n${lines}`)
    err._i18nValidation = { locale, ns, issues }
    throw err
  }
}

function flattenKeys(obj, prefix = '') {
  const out = []
  if (typeof obj === 'string') { out.push(prefix.replace(/^\./, '')); return out }
  if (Array.isArray(obj)) { obj.forEach((v,i)=>out.push(...flattenKeys(v, `${prefix}.${i}`))); return out }
  if (obj && typeof obj === 'object') {
    Object.entries(obj).forEach(([k, v]) => { out.push(...flattenKeys(v, `${prefix}.${k}`)) })
  }
  return out
}

function precompileAll(i18n, locale, ns, msgs) {
  const base = ns ? { [ns]: msgs } : msgs
  const keys = flattenKeys(base)
  for (const k of keys) {
    const fullKey = ns ? `${ns}.${k}` : k
    try {
      i18n.global.t(fullKey, {}, locale)
    } catch (e) {
      console.error('[i18n precompile error]', { locale, key: fullKey, message: e?.message, code: e?.code })
      throw e
    }
  }
}
// --- end helpers ---


// export async function ensureGlobals(locale) {
//   const loc = locale || i18n.global.locale.value || 'en'
//   if (loadedGlobals.has(loc)) return

//   const msgs = (await import(
//     /* webpackChunkName: "i18n-globals-[request]" */
//     `./globals/${loc}.globals.json`
//   )).default

//   const current = i18n.global.getLocaleMessage(loc) || {}

//   // validate + precompile
//   validateMessagesTree(msgs, { locale: loc, ns: 'globals' })
//   precompileAll(i18n, loc, null, msgs)

//   i18n.global.setLocaleMessage(loc, { ...current, ...msgs })
//   loadedGlobals.add(loc)
// }

export async function ensureNs(ns, locale) {
  const loc = locale || i18n.global.locale.value || 'en'
  const key = `${loc}:${ns}`
  if (loadedPages.has(key)) return

  const msgs = (await import(
    /* webpackChunkName: "i18n-pages-[request]" */
    `./pages/${loc}.${ns}.json`
  )).default

  const current = i18n.global.getLocaleMessage(loc) || {}

  // validate + precompile
  validateMessagesTree(msgs, { locale: loc, ns })
  precompileAll(i18n, loc, ns, msgs)

  i18n.global.setLocaleMessage(loc, { ...current, [ns]: msgs })
  loadedPages.add(key)
}

// export function withNs(ns) {
//   return async (to, from, next) => {
//     const loc = (to.params && to.params.locale) || i18n.global.locale.value || 'en'
//     try {
//       await ensureGlobals(loc)  // make sure globals exist first
//       await ensureNs(ns, loc)   // then this page’s bundle
//     } catch (e) {
//       console.error('i18n load error', e)
//     }
//     next()
//   }
// }

export function withNs(ns) {
  return async (to, from, next) => {
    const loc = (to.params && to.params.locale) || i18n.global.locale.value || 'en';
    try {
      await ensureGlobals(loc);
      await ensureNs(ns, loc);
    } catch (e) {
      console.error('i18n load error', e);
      // TEMP: make it visible while debugging
      alert(`i18n load error for ${loc}:${ns}\n${e?.message || e}`);
    }
    next();
  };
}
